import { useState } from "react";
import { FetchResult, gql, useMutation } from "@apollo/client";
import { useToast } from "@/hooks/use-toast";

const UPDATE_MEETING_TASK_MUTATION = gql`
  mutation updateMeetingTask(
    $taskId: String
    $title: String
    $description: String
  ) {
    saveTask(
      Task: { taskId: $taskId, title: $title, description: $description }
    )
  }
`;

const UPDATE_MEETING_TASK_TIME_MUTATION = gql`
  mutation updateMeetingTask(
    $taskId: String
    $dueDate: String
    $duration: String
    $type: String
  ) {
    saveTask(
      Task: {
        taskId: $taskId
        dueDate: $dueDate
        durationTime: $duration
        type: $type
      }
    )
  }
`;

interface TaskDataInput {
  taskId?: string;
  title: string;
  type: string;
  assignee: string;
  owner: string;
  priority: number;
  dueDate: string;
  category: string;
  description: string;
  reference: string;
  durationTime?: string;
}

interface updateMeetingTaskQueryVariables {
  taskId: string;
  title: string;
  description: string;
}

interface updateMeetingTaskTimeQueryVariables {
  taskId: string;
  dueDate: string;
  duration: string;
  type: string;
}

interface updateMeetingTaskQueryResponse {
  saveTask: string;
}

export const useUpdateMeetingTask = () => {
  const [submitting, setSubmitting] = useState(false);
  const { toast } = useToast();
  const [handleUpdateMeetingTask] = useMutation<
    updateMeetingTaskQueryResponse,
    updateMeetingTaskQueryVariables
  >(UPDATE_MEETING_TASK_MUTATION);

  const [handleUpdateMeetingTaskTime] = useMutation<
    updateMeetingTaskQueryResponse,
    updateMeetingTaskTimeQueryVariables
  >(UPDATE_MEETING_TASK_TIME_MUTATION);

  const updateMeetingTask = async (
    taskId: string,
    title: string,
    description: string,
    callback?: () => void
  ) => {
    setSubmitting(true);
    try {
      const apiResponse = await handleUpdateMeetingTask({
        variables: { taskId, title, description },
      });
      handleBadAPIResponse(apiResponse);
      setSubmitting(false);
      callback && callback();
      return apiResponse;
    } catch (e: unknown) {
      console.log("Error updating task:", e);
      toast({
        title: "Error updating task",
        description: "An error occurred while updating the task",
        variant: "destructive",
      });
      setSubmitting(false);
      return null;
    }
  };

  const updateMeetingTaskTime = async (
    taskId: string,
    dueDate: string,
    duration: string,
    type: string,
    callback?: () => void
  ) => {
    setSubmitting(true);
    try {
      const apiResponse = await handleUpdateMeetingTaskTime({
        variables: { taskId, dueDate, duration, type },
      });
      handleBadAPIResponse(apiResponse);
      setSubmitting(false);
      callback && callback();
      return apiResponse;
    } catch (e: unknown) {
      console.log("Error updating task time:", e);
      toast({
        title: "Error updating task time",
        description: "An error occurred while updating the task time",
        variant: "destructive",
      });
      setSubmitting(false);
      return null;
    }
  };

  return { updateMeetingTask, updateMeetingTaskTime, submitting };
};

const handleBadAPIResponse = (
  apiResponse: FetchResult<
    updateMeetingTaskQueryResponse,
    Record<string, unknown>,
    Record<string, unknown>
  >
) => {
  if (!apiResponse.data || !apiResponse.data.saveTask) {
    throw new Error("Error while saving task");
  }
};
